import React, { useState } from "react";
import {
  Spinner,
  Modal,
  Button,
  Table,
  Pagination,
  Col,
  Row,
} from "react-bootstrap";
import { BsEye } from "react-icons/bs";

import { InvestorProfileData } from "../Models/InvestorProfileData";
import { useGetInvestors } from "../../api/queries/MuslimInvest/useGetInvestors";

const Investors = () => {
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [page, setPage] = useState(1);
  const pageSize = 10;

  const { data, isPending } = useGetInvestors({ pageSize, page });
  const investors = data?.investorProfile || [];

  const totalRecords = data?.totalRecords || 0;
  const totalPages = Math.ceil(totalRecords / pageSize);

  const [selectedInvestor, setSelectedInvestor] =
    useState<InvestorProfileData | null>(null);

  const openModal = (investor: InvestorProfileData) => {
    setSelectedInvestor(investor);
    setShowModal(true);
  };

  const closeModal = () => {
    setSelectedInvestor(null);
    setShowModal(false);
  };

  const openDeleteModal = (investor: InvestorProfileData) => {
    setSelectedInvestor(investor);
    setShowDeleteModal(true);
  };

  const handleDelete = () => {};

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPage(newPage);
  };

  return (
    <div className="container py-4">
      <h2 className="fw-bold mb-4">Muslim Invest – Investor Profiles</h2>

      {/* Loader */}
      {isPending && (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" />
        </div>
      )}

      {/* No Data */}
      {!isPending && investors.length === 0 && (
        <div className="alert alert-info text-center">No investors found</div>
      )}

      {/* Table */}
      {!isPending && investors.length > 0 && (
        <>
          <Table
            striped
            bordered
            hover
            responsive
            className="rounded-4 shadow-sm"
          >
            <thead className="table-light">
              <tr>
                <th>ID</th>
                <th>Investment Type</th>
                <th>Sectors of Interest</th>
                <th>Stage</th>
                <th>Status</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>

            <tbody>
              {investors.map((inv) => (
                <tr key={inv.id}>
                  <td>{inv.id}</td>
                  <td>{inv.investmentType || "-"}</td>
                  <td>{inv.sectorsOfInterest || "-"}</td>
                  <td>{inv.developmentStage || "-"}</td>
                  <td>{inv.status || "-"}</td>

                  <td className="d-flex gap-2 justify-content-center">
                    <BsEye
                      size={20}
                      className="text-primary cursor-pointer"
                      onClick={() => openModal(inv)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {/* Pagination */}
          <div className="d-flex justify-content-center mt-3">
            <Pagination>
              <Pagination.Prev
                disabled={page === 1}
                onClick={() => handlePageChange(page - 1)}
              />
              {Array.from({ length: totalPages }, (_, i) => (
                <Pagination.Item
                  key={i + 1}
                  active={i + 1 === page}
                  onClick={() => handlePageChange(i + 1)}
                >
                  {i + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next
                disabled={page === totalPages}
                onClick={() => handlePageChange(page + 1)}
              />
            </Pagination>
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        centered
        onHide={() => setShowDeleteModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {selectedInvestor && (
            <p>
              Are you sure you want to delete investor{" "}
              <strong>ID {selectedInvestor.id}</strong>?
            </p>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            className="rounded-pill"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </Button>

          <Button
            variant="danger"
            className="rounded-pill"
            onClick={handleDelete}
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Investor Details Modal */}
      <Modal show={showModal} onHide={closeModal} centered size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Investor Details</Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
          {selectedInvestor && (
            <div className="p-2">
              {/* GENERAL INFORMATION */}
              <div className="card shadow-sm border-0 mb-3 p-3">
                <h5 className="fw-semibold mb-3">General Information</h5>

                <Row>
                  <Col md={6} className="mb-3">
                    <strong>Investment Type</strong>
                    <div className="text-muted small">
                      {selectedInvestor.investmentType || "-"}
                    </div>
                  </Col>

                  <Col md={6} className="mb-3">
                    <strong>Sectors Of Interest</strong>
                    <div className="text-muted small">
                      {selectedInvestor.sectorsOfInterest || "-"}
                    </div>
                  </Col>

                  <Col md={6} className="mb-3">
                    <strong>Development Stage</strong>
                    <div className="text-muted small">
                      {selectedInvestor.developmentStage || "-"}
                    </div>
                  </Col>
                </Row>
              </div>

              {/* INVESTMENT PREFERENCES */}
              <div className="card shadow-sm border-0 mb-3 p-3">
                <h5 className="fw-semibold mb-3">Investment Preferences</h5>

                <Row>
                  <Col md={6} className="mb-3">
                    <strong>Frequency of Updates</strong>
                    <div className="text-muted small">
                      {selectedInvestor.frequencyOfUpdates || "-"}
                    </div>
                  </Col>

                  <Col md={6} className="mb-3">
                    <strong>Presentation Preferences</strong>
                    <div className="text-muted small">
                      {selectedInvestor.presentationPreferences || "-"}
                    </div>
                  </Col>

                  <Col md={6} className="mb-3">
                    <strong>Investment Horizon</strong>
                    <div className="text-muted small">
                      {selectedInvestor.investmentTimeHorizon || "-"}
                    </div>
                  </Col>

                  <Col md={6} className="mb-3">
                    <strong>Ticket Size</strong>
                    <div className="text-muted small">
                      {selectedInvestor.investmentTicketSize || "-"}
                    </div>
                  </Col>
                </Row>
              </div>

              {/* EXPERIENCE + TAX + THIRD PARTY */}
              <div className="card shadow-sm border-0 mb-3 p-3">
                <h5 className="fw-semibold mb-3">Experience & Compliance</h5>

                <Row>
                  <Col md={6} className="mb-3">
                    <strong>Previous Investments</strong>
                    <div className="text-muted small">
                      {selectedInvestor.numberOfPreviousInvestments || "-"}
                    </div>
                  </Col>

                  <Col md={6} className="mb-3">
                    <strong>Experience with EIS/SEIS</strong>
                    <div className="text-muted small">
                      {selectedInvestor.isExperienceWithEISorSEIS
                        ? "Yes"
                        : "No"}
                    </div>
                  </Col>

                  <Col md={6} className="mb-3">
                    <strong>Tax Breaks Important</strong>
                    <div className="text-muted small">
                      {selectedInvestor.isTaxBreaksImportant ? "Yes" : "No"}
                    </div>
                  </Col>

                  <Col md={6} className="mb-3">
                    <strong>Require 3rd Party Review</strong>
                    <div className="text-muted small">
                      {selectedInvestor.isRequireThirdPartyReview
                        ? "Yes"
                        : "No"}
                    </div>
                  </Col>

                  <Col md={6}>
                    <strong>Using 3rd Party for Transactions</strong>
                    <div className="text-muted small">
                      {selectedInvestor.isUsingThirdPartyForTransactions
                        ? "Yes"
                        : "No"}
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeModal}
            className="rounded-pill"
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Investors;
